import { useEffect } from 'react';
import { toast } from 'sonner';
import { useAuthStore } from '@/auth/auth-store';

/**
 * Browser Notification API permission request.
 *
 * Mirrors Flutter `menu_container_screen.dart:212-230` — Flutter uses
 * a 1s `Future.delayed` after auth boot to request permission. **That
 * pattern doesn't work in modern browsers** (Chrome/Safari require
 * `requestPermission()` to originate from a user gesture, otherwise the
 * call is auto-denied without prompting). Flutter mobile bypasses this
 * because mobile OS permission dialogs are native-platform calls, not
 * browser ones.
 *
 * **React adaptation (post-Samer review on PR #292)**: gate the request
 * on the user's FIRST interaction with the page (any click or keydown)
 * after auth boot. This guarantees a user gesture is on the call stack
 * when `requestPermission()` fires. Once the user has answered (granted
 * or denied), the listener self-removes — exactly one ask per session.
 *
 * **Phase 1 scope only.** Full Firebase Cloud Messaging integration —
 * service worker registration, vapidKey, backend token registration,
 * background message handling — is deferred to Phase 3.
 *
 * If the browser doesn't support Notification API (older browsers,
 * iframes), the effect is a silent no-op.
 *
 * Renders nothing.
 */

export function PushPermissionIntegration() {
  const user = useAuthStore((s) => s.user);

  // Permission request — gated on first user interaction after auth.
  useEffect(() => {
    if (!user) return;
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'default') return;

    let cleaned = false;
    const askOnce = () => {
      if (cleaned) return;
      cleaned = true;
      document.removeEventListener('click', askOnce);
      document.removeEventListener('keydown', askOnce);
      // Now we have a user gesture on the stack — browsers will accept
      // the prompt.
      Notification.requestPermission().catch(() => {
        // silent — user may have rejected via OS settings or browser
        // may not support the prompt-from-listener pattern. Either
        // way, no toast — push delivery just stays disabled.
      });
    };

    document.addEventListener('click', askOnce, { once: false });
    document.addEventListener('keydown', askOnce, { once: false });

    return () => {
      cleaned = true;
      document.removeEventListener('click', askOnce);
      document.removeEventListener('keydown', askOnce);
    };
  }, [user?.userId]);

  // Foreground listener stub. Phase 3 FCM will replace this with
  // `onMessage(messaging, ...)`; for Phase 1 we wire the listener to
  // an internal CustomEvent dispatch so domain code (e.g. Phase 2
  // Notification Center) can `window.dispatchEvent(new CustomEvent
  // ('scrips:push', {detail: {title, body}}))` and get a toast for
  // free during development.
  useEffect(() => {
    function handler(e: Event) {
      const ce = e as CustomEvent<{ title: string; body?: string }>;
      const { title, body } = ce.detail ?? { title: '' };
      if (!title) return;
      toast(title, { description: body });
    }
    window.addEventListener('scrips:push', handler as EventListener);
    return () =>
      window.removeEventListener('scrips:push', handler as EventListener);
  }, []);

  return null;
}
